import { createContext, useContext, useEffect, useState } from 'react';

/**
 * ThemeContext for multi-tenant theming.
 * Sets data-theme attribute on document root based on user's organization.
 */

const ThemeContext = createContext(null);

/**
 * ThemeProvider wraps the app and manages the current theme.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.theme - Theme identifier ('invol', 'drboese')
 * @param {string} props.organizationName - Display name for the organization
 * @param {string|null} props.logoUrl - URL to organization logo
 */
export function ThemeProvider({
  children,
  theme = 'invol',
  organizationName = 'Invol UG',
  logoUrl = null,
}) {
  const [currentTheme, setCurrentTheme] = useState(theme);

  // Update theme when prop changes (e.g., on login)
  useEffect(() => {
    setCurrentTheme(theme);
  }, [theme]);

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);

    // Clean up on unmount
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [currentTheme]);

  const value = {
    theme: currentTheme,
    setTheme: setCurrentTheme,
    organizationName,
    logoUrl,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context.
 *
 * @returns {{ theme: string, setTheme: (theme: string) => void, organizationName: string, logoUrl: string|null }}
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Resolve logo URL to full URL.
 * In local development, static files are served from the backend (port 8000).
 *
 * @param {string|null} logoUrl - Logo URL (relative or absolute)
 * @returns {string|null}
 */
function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;

  // Already a full URL
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }

  // Relative URL starting with /static/ - prepend backend URL in development
  if (logoUrl.startsWith('/static/')) {
    // In development, static files are served from backend (port 8000)
    // In production, they're served from the same domain
    const isDev = import.meta.env.DEV;
    if (isDev) {
      return `http://localhost:8000${logoUrl}`;
    }
  }

  return logoUrl;
}

/**
 * Extract theme info from user object.
 * Helper function to get theme data from API response.
 *
 * @param {Object|null} user - User object from API
 * @returns {{ theme: string, organizationName: string, logoUrl: string|null }}
 */
export function getThemeFromUser(user) {
  if (!user?.organization) {
    return {
      theme: 'invol',
      organizationName: 'Invol UG',
      logoUrl: null,
    };
  }

  return {
    theme: user.organization.theme || 'invol',
    organizationName: user.organization.display_name || user.organization.name || 'Invol UG',
    logoUrl: resolveLogoUrl(user.organization.logo_url),
  };
}
